import React, { useEffect, useState } from "react";

import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  ScrollArea,
  Table,
  Text,
} from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { useSelector } from "react-redux";
import {
  IconAlertTriangle,
  IconDownload,
  IconFileTypePdf,
  IconFileText,
  IconPill,
} from "@tabler/icons-react";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import moment from "moment";

import useStyles from "./style";
import UserDetail from "../MarTab/UserDetail";
import CustomPagination from "../../../../components/Pagination/CustomPagination";
import TableLoader from "../../../../components/TableLoader";
import NotFound from "../NotFound";
import DiscontinueModal from "../../../../components/DiscontinueModal";
import RefillDrawerStepper from "../../../../components/RefillDrawerStepper";
import AddMedicationButton from "../../../../components/AddMedicationButton/AddMedicationButton";
import MedicationListPDF from "../../../../components/MedicationListPDF";
import NarcoticsReportPDF from "../../../../components/NarcoticsReportPDF";
import { decrypt, formatDate, showError } from "../../../../utils";
import {
  deleteMedication,
  getSinglePatients,
} from "../../../../services/patient";
import { getMar } from "../../../../services";
import { isStaff, https } from "../../../../http";

// ── Stock helpers ─────────────────────────────────────────────────────────────

// Fallback for medications that pre-date the avgDailyUse backend field.
// Uses row.timeSlots (form3 string array, always loaded) — no MAR doc needed.
const inferAvgDailyUse = (row) => {
  const slots = Array.isArray(row?.timeSlots) ? row.timeSlots : [];
  const slotCount = slots.length || 1;
  const dosePerSlot =
    Number(row?.medicationadministered || row?.medicationAdministered) || 1;
  const qtyPerDay = slotCount * dosePerSlot;
  const scheduleType = row?.scheduleType || "daily";
  if (scheduleType === "daily") return qtyPerDay;
  const repeatType = row?.repeatType || "day";
  const repeatEvery = Number(row?.repeatEvery) || 1;
  if (repeatType === "day") return qtyPerDay / repeatEvery;
  if (repeatType === "week") {
    const days = Array.isArray(row?.repeatOn) ? row.repeatOn.length : 1;
    return (days * qtyPerDay) / (repeatEvery * 7);
  }
  if (repeatType === "month") return qtyPerDay / (repeatEvery * 30);
  return qtyPerDay;
};

const getStockStatus = (row) => {
  const notTracked = {
    tone: "neutral",
    label: "Not tracked",
    textColor: "#52525B",
    bgColor: "#F2F2F2",
    dotColor: "#A8A8A8",
    daysSupply: null,
  };

  // PRN medications have no fixed schedule — days supply is unpredictable
  const medType = row?.medicationType;
  const isPRN =
    medType === "PRN" ||
    (typeof medType === "string" && medType.toUpperCase().includes("PRN")) ||
    (Array.isArray(medType) &&
      medType.length > 0 &&
      typeof medType[medType.length - 1]?.status === "string" &&
      medType[medType.length - 1].status.toUpperCase().includes("PRN"));
  if (isPRN) return notTracked;

  // Unlimited or missing stock data — opt out
  if (
    row?.medicineRemaining === "unlimited" ||
    row?.totalMedicine === "unlimited" ||
    row?.isUnlimited === true
  )
    return notTracked;

  const current = Number(row?.medicineRemaining);
  const total = Number(row?.totalMedicine);

  if (isNaN(current) || isNaN(total) || total === 0) return notTracked;

  // Custom schedules: count only scheduled administration days, not calendar days.
  // Divisor = doses taken per scheduled day (slotCount × dosePerSlot), ignoring repeatEvery/repeatOn.
  // Daily schedules: use the time-averaged daily rate as before.
  const scheduleType = row?.scheduleType || "daily";
  let daysSupplyDivisor;
  if (scheduleType === "custom") {
    const slots = Array.isArray(row?.timeSlots) ? row.timeSlots : [];
    const slotCount = slots.length || 1;
    const dosePerSlot =
      Number(row?.medicationadministered || row?.medicationAdministered) || 1;
    daysSupplyDivisor = slotCount * dosePerSlot;
  } else {
    daysSupplyDivisor = Number(row?.avgDailyUse) || inferAvgDailyUse(row);
  }
  const daysSupply =
    daysSupplyDivisor > 0 && current > 0
      ? Math.max(1, Math.round(current / daysSupplyDivisor))
      : daysSupplyDivisor > 0 && current === 0
        ? 0
        : null;

  if (daysSupply === null) return notTracked;

  if (daysSupply <= 3)
    return {
      tone: "danger",
      label: "Critical",
      textColor: "#B91C1C",
      bgColor: "#FEE2E2",
      dotColor: "#DC2626",
      daysSupply,
    };
  if (daysSupply <= 7)
    return {
      tone: "warning",
      label: "Low",
      textColor: "#92400E",
      bgColor: "#FEF3C7",
      dotColor: "#D97706",
      daysSupply,
    };
  if (daysSupply <= 14)
    return {
      tone: "yellow",
      label: "Watch",
      textColor: "#92400E",
      bgColor: "#FFF3DC",
      dotColor: "#FFBA45",
      daysSupply,
    };
  return {
    tone: "success",
    label: "OK",
    textColor: "#15803D",
    bgColor: "#DCFCE7",
    dotColor: "#16A34A",
    daysSupply,
  };
};

const getStockBarColor = (tone) => {
  const map = {
    danger: "#DC2626",
    warning: "#D97706",
    yellow: "#FFBA45",
    success: "#16A34A",
    neutral: "#A8A8A8",
  };
  return map[tone] || "#A8A8A8";
};

// ── Main component ────────────────────────────────────────────────────────────

const MedicationListTab = ({
  userId,
  clientId,
  clientDetails,
  onEditClick,
}) => {
  const { classes, cx } = useStyles();

  const [scrolled, setScrolled] = useState(false);
  const [rows, setRows] = useState([]);
  const [rowsCopy, setRowsCopy] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectDiscontinue, setSelectDiscontinue] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [narcoticPdfLoading, setNarcoticPdfLoading] = useState(false);
  const [narcMonthPickerOpen, setNarcMonthPickerOpen] = useState(false);
  const [narcSelectedMonth, setNarcSelectedMonth] = useState(new Date());
  const [isActive, setIsActive] = useState(true);
  const [activeMedicines, setActiveMedicines] = useState([]);

  const { selectedClient } = useSelector((s) => s.mar);
  const { allPatients } = useSelector((s) => s.patient);
  const { loading } = useSelector((s) => s.patient);
  const { profile } = useSelector((s) => s?.profile);
  const allFacilities = useSelector((s) => s?.facility);

  const inActiveMedicinesData = Array.isArray(rows)
    ? rows.filter((r) => r?.discontinued === true)
    : [];
  const activeMedicinesData = Array.isArray(rows)
    ? rows.filter((r) => r?.discontinued !== true)
    : [];

  const activeInActiveMedicines = (bool) => {
    setIsActive(bool);
    setCurrentPage(1);
  };

  useEffect(() => {
    const filtered = isActive ? activeMedicinesData : inActiveMedicinesData;
    setActiveMedicines(
      filtered.slice((currentPage - 1) * 10, currentPage * 10),
    );
  }, [currentPage, isActive, rowsCopy]);

  useEffect(() => {
    if (selectedClient?.form3?.length > 0) {
      setRows(selectedClient.form3);
      setRowsCopy(selectedClient.form3);
    } else {
      setRows([]);
      setRowsCopy([]);
    }
  }, [selectedClient]);

  // Fetch all MAR docs (not date-filtered) so every medication's latest state is available
  useEffect(() => {
    if (selectedClient?.id) {
      const page = "all";
      getMar(page, selectedClient.id, false);
    }
  }, [selectedClient?.id]);

  // ── PDF handlers ────────────────────────────────────────────────────────────

  const handleMedListPdf = async () => {
    if (!rows?.length) return;
    setPdfLoading(true);
    try {
      const facilityName =
        profile?.business?.companyName ||
        selectedClient?.facility?.name ||
        (Array.isArray(allFacilities?.facilities)
          ? allFacilities.facilities.find(
              (f) => f.id === selectedClient?.facilityId,
            )?.name
          : null) ||
        profile?.facility?.name ||
        "Facility";
      const facilityPhoto = decrypt(
        selectedClient?.facility?.imageurl ||
          (Array.isArray(allFacilities?.facilities)
            ? allFacilities.facilities.find(
                (f) => f.id === selectedClient?.facilityId,
              )?.imageurl
            : null) ||
          profile?.facility?.imageurl,
      );
      const rawName = selectedClient?.name || "";
      const clientName = rawName ? decrypt(rawName) || rawName : "Client";
      const blob = await pdf(
        <MedicationListPDF
          clientData={{ ...selectedClient, name: clientName }}
          medications={rows}
          facilityName={facilityName}
          facilityPhoto={facilityPhoto}
        />,
      ).toBlob();
      saveAs(
        blob,
        `Medication_List_${clientName.replace(/\s+/g, "_")}_${moment().format("YYYY-MM-DD")}.pdf`,
      );
    } catch (err) {
      console.error("PDF generation error:", err);
    }
    setPdfLoading(false);
  };

  const handleNarcoticsPdfClick = () => {
    setNarcSelectedMonth(new Date());
    setNarcMonthPickerOpen(true);
  };

  const handleNarcoticsPdfGenerate = async () => {
    setNarcMonthPickerOpen(false);
    setNarcoticPdfLoading(true);
    try {
      const reportMonth = moment(narcSelectedMonth).month() + 1;
      const reportYear = moment(narcSelectedMonth).year();
      const monthStart = moment(narcSelectedMonth).startOf("month");
      const monthEnd = moment(narcSelectedMonth).endOf("month");

      const narcotics = rows?.filter((r) => {
        if (r?.isNarcotic !== "Yes" && r?.isNarcotic !== true) return false;
        if (!r?.discontinued) return true;
        const discDate =
          r?.discontinuedDate ||
          r?.discontinuedAt ||
          r?.discontinuedOn ||
          r?.updatedAt;
        if (!discDate) return false;
        return moment(discDate).isSameOrAfter(monthStart, "month");
      });

      if (!narcotics?.length) {
        setNarcoticPdfLoading(false);
        showError(
          `No narcotic medications were active during ${moment(narcSelectedMonth).format("MMMM YYYY")}. Select a different month or check the medication list.`,
          null,
          false,
        );
        return;
      }

      const facilityName =
        selectedClient?.facility?.name ||
        (Array.isArray(allFacilities?.facilities)
          ? allFacilities.facilities.find(
              (f) => f.id === selectedClient?.facilityId,
            )?.name
          : null) ||
        profile?.facility?.name ||
        "Facility";
      const facilityPhoto = decrypt(
        selectedClient?.facility?.imageurl ||
          (Array.isArray(allFacilities?.facilities)
            ? allFacilities.facilities.find(
                (f) => f.id === selectedClient?.facilityId,
              )?.imageurl
            : null) ||
          profile?.facility?.imageurl,
      );
      const rawName2 = selectedClient?.name || "";
      const clientName = rawName2 ? decrypt(rawName2) || rawName2 : "Client";

      let marRecords = [];
      try {
        const clientIdForMar = selectedClient?.id || selectedClient?._id;
        const pageStr = `monthly_${monthStart.format("YYYY-MM-DD")}_${monthEnd.format("YYYY-MM-DD")}`;
        const res = await https().get(
          `/mar/getCalendarView?client=${clientIdForMar}&page=${pageStr}`,
        );
        marRecords = res?.data?.data?.entries || [];
      } catch (marErr) {
        console.warn(
          "Could not fetch MAR records for narcotics report:",
          marErr,
        );
      }

      const blob = await pdf(
        <NarcoticsReportPDF
          clientData={{ ...selectedClient, name: clientName }}
          narcoticMeds={narcotics}
          marRecords={marRecords}
          facilityName={facilityName}
          facilityPhoto={facilityPhoto}
          reportMonth={reportMonth}
          reportYear={reportYear}
        />,
      ).toBlob();
      const monthLabel = moment(narcSelectedMonth).format("MMMM_YYYY");
      saveAs(
        blob,
        `Narcotics_Log_${clientName.replace(/\s+/g, "_")}_${monthLabel}.pdf`,
      );
    } catch (err) {
      console.error("Narcotics PDF generation error:", err);
      showError(
        "Failed to generate the Narcotics Log PDF. Please try again.",
        null,
        false,
      );
    }
    setNarcoticPdfLoading(false);
  };

  // ── Counts ──────────────────────────────────────────────────────────────────

  const totalMeds = rows?.length || 0;
  const activeCount = activeMedicinesData?.length || 0;
  const inactiveCount = inActiveMedicinesData?.length || 0;
  const narcoticCount =
    rows?.filter((r) => r?.isNarcotic === "Yes" || r?.isNarcotic === true)
      ?.length || 0;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={classes.content}>
      {/* Toolbar */}
      <Box className={classes.tabsAndBtnBar}>
        {/* Active / Inactive toggle */}
        <div className={classes.activeInactiveParent}>
          {[
            { id: "active", label: "Active" },
            { id: "inactive", label: "Inactive" },
          ].map((t) => (
            <button
              key={t.id}
              className={`${classes.btnCommonCSS} ${(t.id === "active" ? isActive : !isActive) ? classes.activeBg : ""}`}
              onClick={() => activeInActiveMedicines(t.id === "active")}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Action buttons */}
        <Group spacing={8}>
          <Button
            variant="outline"
            size="xs"
            leftIcon={
              pdfLoading ? (
                <Loader size={14} color="dark" />
              ) : (
                <IconFileTypePdf size={16} />
              )
            }
            onClick={handleMedListPdf}
            disabled={pdfLoading || !rows?.length}
            styles={{
              root: {
                borderColor: "#D4D4D4",
                color: "#262626",
                fontWeight: 600,
                fontFamily: "Inter",
                fontSize: 13,
                height: 36,
                "&:hover": { backgroundColor: "#F5F5F5" },
              },
            }}
          >
            Medication List
          </Button>
          <Button
            variant="outline"
            size="xs"
            leftIcon={
              narcoticPdfLoading ? (
                <Loader size={14} color="red" />
              ) : (
                <IconAlertTriangle size={16} />
              )
            }
            onClick={handleNarcoticsPdfClick}
            disabled={narcoticPdfLoading || narcoticCount === 0}
            styles={{
              root: {
                borderColor: "#FECACA",
                color: "#B91C1C",
                fontWeight: 600,
                fontFamily: "Inter",
                fontSize: 13,
                height: 36,
                "&:hover": { backgroundColor: "#FEF2F2" },
              },
            }}
          >
            Narcotics Log
          </Button>
          <AddMedicationButton
            userId={userId}
            selectedClient={selectedClient}
          />
        </Group>
      </Box>

      {/* Stats bar */}
      <Flex className={classes.statsBar}>
        <Flex align="center" gap={6}>
          <Text size="xs" color="dimmed" weight={500}>
            Total
          </Text>
          <Badge size="sm" variant="filled" color="dark" radius="sm">
            {totalMeds}
          </Badge>
        </Flex>
        <Box className={classes.statsDivider} />
        <Flex align="center" gap={6}>
          <span
            className={classes.legendDot}
            style={{ background: "#16A34A" }}
          />
          <Text size="xs" color="dimmed" weight={500}>
            Active
          </Text>
          <Badge size="sm" variant="light" color="green" radius="sm">
            {activeCount}
          </Badge>
        </Flex>
        <Flex align="center" gap={6}>
          <span
            className={classes.legendDot}
            style={{ background: "#9E9E9E" }}
          />
          <Text size="xs" color="dimmed" weight={500}>
            Inactive
          </Text>
          <Badge size="sm" variant="light" color="gray" radius="sm">
            {inactiveCount}
          </Badge>
        </Flex>
        {narcoticCount > 0 && (
          <Flex align="center" gap={6}>
            <span
              className={classes.legendDot}
              style={{ background: "#DC2626" }}
            />
            <Text size="xs" color="dimmed" weight={500}>
              Narcotics
            </Text>
            <Badge size="sm" variant="light" color="red" radius="sm">
              {narcoticCount}
            </Badge>
          </Flex>
        )}
      </Flex>

      {/* Body */}
      <div className={classes.scheduleDetail}>
        {!userId && <UserDetail isClient={false} />}

        <div style={{ width: "100%", overflowX: "auto" }}>
          <div className={classes.wrapper}>
            {loading ? (
              <Box
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "100px 0",
                }}
              >
                <Loader color="yellow" size="md" />
              </Box>
            ) : (
              <div className={classes.mainContainer}>
                <Box className={classes.tableContainer}>
                  <ScrollArea
                    onScrollPositionChange={({ y }) => setScrolled(y !== 0)}
                    className={classes.scrollArea}
                  >
                    <Table
                      highlightOnHover={false}
                      sx={{
                        fontFamily: "Inter",
                        minWidth: "1400px",
                        borderCollapse: "collapse",
                      }}
                      className={classes.table}
                    >
                      <TableHeader scrolled={scrolled} />
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan={1000}>
                              <TableLoader />
                            </td>
                          </tr>
                        ) : activeMedicines?.length > 0 ? (
                          <TableRow
                            data={activeMedicines}
                            open={open}
                            clientId={clientId}
                            onEditClick={onEditClick}
                            setSelectDiscontinue={setSelectDiscontinue}
                          />
                        ) : (
                          <NotFound />
                        )}
                      </tbody>
                    </Table>
                  </ScrollArea>
                </Box>

                <Flex justify="flex-end" mt={8}>
                  <CustomPagination
                    data={
                      isActive
                        ? activeMedicinesData?.length
                        : inActiveMedicinesData?.length
                    }
                    itemsPerPage={10}
                    currentPage={currentPage}
                    handlePageChange={(page) => setCurrentPage(page)}
                  />
                </Flex>

                <DiscontinueModal
                  opened={opened}
                  onclose={close}
                  data={selectDiscontinue}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Narcotics report month picker modal */}
      <Modal
        opened={narcMonthPickerOpen}
        onClose={() => setNarcMonthPickerOpen(false)}
        title="Generate Narcotics Log"
        centered
        size="sm"
        styles={{
          title: { fontWeight: 700, fontFamily: "Inter" },
          body: { overflow: "visible" },
          content: { overflow: "visible" },
        }}
        overlayProps={{ zIndex: 1000 }}
        transitionProps={{ duration: 200 }}
        radius="md"
        zIndex={1000}
      >
        <Text size="sm" color="dimmed" mb={16}>
          Select the month for the Controlled Substance &amp; Narcotic Monthly
          Count report.
        </Text>
        <MonthPickerInput
          label="Report Month"
          placeholder="Select month"
          value={narcSelectedMonth}
          onChange={setNarcSelectedMonth}
          maxDate={new Date()}
          mb={20}
          popoverProps={{ withinPortal: true, zIndex: 1000 }}
          styles={{
            input: { fontFamily: "Inter" },
            label: { fontWeight: 600, fontFamily: "Inter" },
          }}
        />
        <Flex justify="flex-end" gap={8}>
          <Button
            variant="outline"
            color="gray"
            onClick={() => setNarcMonthPickerOpen(false)}
          >
            Cancel
          </Button>
          <Button
            color="red"
            leftIcon={<IconAlertTriangle size={16} />}
            onClick={handleNarcoticsPdfGenerate}
            loading={narcoticPdfLoading}
          >
            Generate Report
          </Button>
        </Flex>
      </Modal>
    </div>
  );
};

// ── TableHeader ───────────────────────────────────────────────────────────────

const TableHeader = ({ scrolled }) => {
  const { classes, cx } = useStyles();
  return (
    <thead className={cx(classes.header, { [classes.scrolled]: scrolled })}>
      <tr>
        <th style={{ minWidth: 220 }}>Medication</th>
        <th style={{ minWidth: 180 }}>Stock</th>
        <th style={{ minWidth: 130 }}>Days Supply</th>
        <th style={{ minWidth: 160 }}>Pharmacy</th>
        <th style={{ minWidth: 150 }}>Last Refill</th>
        <th style={{ minWidth: 100 }}>Refills Left</th>
        <th style={{ minWidth: 140 }}>Doctor's Order</th>
        <th style={{ minWidth: 80, textAlign: "right" }}>Actions</th>
      </tr>
    </thead>
  );
};

// ── TableRow ──────────────────────────────────────────────────────────────────

const TableRow = ({
  data,
  onEditClick,
  setSelectDiscontinue,
  open,
  clientId,
}) => {
  console.log("🚀 ~ TableRow ~ data:", data);
  const { classes } = useStyles();
  const { profile } = useSelector((s) => s?.profile);
  const { addPatientLoading } = useSelector((s) => s?.patient);
  const { selectedClient } = useSelector((s) => s.mar);

  const [refillRow, setRefillRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [doctorOrderModal, setDoctorOrderModal] = useState(null);

  const getFileType = (url) => {
    const ext = url?.split("?")[0]?.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext))
      return "image";
    if (ext === "pdf") return "pdf";
    if (["mp4", "webm", "ogg", "mov"].includes(ext)) return "video";
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext))
      return "office";
    if (ext === "html" || ext === "htm") return "html";
    return "other";
  };

  const getFileName = (url) =>
    url?.split("?")[0]?.split("/").pop() || "Doctor's Order";

  const downloadFile = async (url) => {
    try {
      if (!url) return;
      const response = await fetch(url);
      const blob = await response.blob();
      const filename = url.split("/").pop();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const remove = async (medicationId, name) => {
    await deleteMedication({ clientId, medicationId, medicationName: name });
    await getMar(localStorage.getItem("calendar_date"), clientId);
  };

  return (
    <>
      {data?.map((row, i) => {
        const isNarcotic =
          row?.isNarcotic === "Yes" || row?.isNarcotic === true;

        // PRN comes from medicationType string, not a boolean field
        const isPRN = (() => {
          const mt = row?.medicationType;
          if (!mt) return false;
          if (mt === "PRN") return true;
          if (typeof mt === "string") return mt.toUpperCase().includes("PRN");
          if (Array.isArray(mt) && mt.length > 0) {
            const last = mt[mt.length - 1];
            const s = typeof last?.status === "string" ? last.status : "";
            return s.toUpperCase().includes("PRN");
          }
          return false;
        })();

        const isDiscontinued = "discontinued" in row;

        const noCount =
          row?.medicineRemaining === "unlimited" ||
          row?.totalMedicine === "unlimited" ||
          row?.isUnlimited === true;

        const current = Number(row?.medicineRemaining) || 0;
        const total = Number(row?.totalMedicine) || 0;
        const meterPct =
          total > 0 ? Math.max(2, Math.min(100, (current / total) * 100)) : 0;
        const barColor =
          meterPct <= 20
            ? "#DC2626"
            : meterPct <= 40
              ? "#D97706"
              : meterPct <= 60
                ? "#FFBA45"
                : "#16A34A";

        const stock = getStockStatus(row);

        // Pharmacy: use field if present, else fall back to a static dash
        const pharmacy =
          row?.pharmacy ||
          row?.pharmacyName ||
          selectedClient?.form2?.pharmacyName ||
          "-";

        // Last refill: only populated after a refill has been recorded
        const lastRefillDate = row?.lastRefillDate
          ? formatDate(row.lastRefillDate)
          : null;
        const lastRefillBy = row?.lastRefillBy || null;

        // Refills remaining: field or dash
        const refillsRemaining =
          row?.refillsLeft !== undefined &&
          row?.refillsLeft !== "" &&
          row?.refillsLeft !== null
            ? row.refillsLeft
            : "-";

        // Doctor's order file
        const doctorOrderUrl = row?.doctorNote || null;

        return (
          <tr
            key={i}
            style={{ cursor: "pointer" }}
            onClick={() => onEditClick?.(row, true)}
          >
            {/* ── Medication ── */}
            <td onClick={(e) => e.stopPropagation()}>
              <Flex align="center" gap={10}>
                {/* Pill icon */}
                <div
                  className={`${classes.pillIcon} ${isNarcotic ? classes.pillIconNarcotic : classes.pillIconNormal}`}
                >
                  <IconPill
                    size={16}
                    color={isNarcotic ? "#B91C1C" : "#92400E"}
                  />
                </div>
                <div>
                  {/* Line 1: medication name */}
                  <span className={classes.medName}>
                    {row?.medicationName || "-"}
                  </span>
                  {/* Line 2: strength (strengthAndForm or dosage + unit) */}
                  {(row?.strengthAndForm || (row?.dosage && row?.unit)) && (
                    <div className={classes.medStrength}>
                      {row?.strengthAndForm || `${row?.dosage} ${row?.unit}`}
                    </div>
                  )}
                  {/* Line 3: chips — Narcotics · PRN · No count */}
                  {(isNarcotic || isPRN || noCount) && (
                    <Flex align="center" gap={4} mt={5} wrap="wrap">
                      {isNarcotic && (
                        <span
                          className={`${classes.chip} ${classes.chipNarcotic}`}
                        >
                          Narcotics
                        </span>
                      )}
                      {isPRN && (
                        <span className={`${classes.chip} ${classes.chipPRN}`}>
                          PRN
                        </span>
                      )}
                      {noCount && (
                        <span
                          className={`${classes.chip} ${classes.chipNoCount}`}
                        >
                          No count
                        </span>
                      )}
                    </Flex>
                  )}
                  {/* Line 4: dosage form · route · frequency */}
                  <div className={classes.medSubtitle}>
                    {[row?.dosageForm, row?.ingestionMode, row?.frequency]
                      .filter(Boolean)
                      .join(" · ") || "-"}
                  </div>
                </div>
              </Flex>
            </td>

            {/* ── Stock ── */}
            <td onClick={(e) => e.stopPropagation()}>
              {noCount ? (
                <Text
                  size="xs"
                  color="#A3A3A3"
                  italic
                  style={{ fontFamily: "Inter" }}
                >
                  Not tracked by count
                </Text>
              ) : (
                <Flex align="center" gap={10}>
                  <div className={classes.stockBarTrack}>
                    <div
                      className={classes.stockBarFill}
                      style={{
                        width: `${meterPct}%`,
                        background: noCount ? "#A8A8A8" : barColor,
                      }}
                    />
                  </div>
                  <Text
                    size="sm"
                    weight={600}
                    color="#171717"
                    style={{
                      fontFamily: "Inter",
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {current}/{total}
                  </Text>
                </Flex>
              )}
            </td>

            {/* ── Days Supply ── */}
            <td onClick={(e) => e.stopPropagation()}>
              {noCount ? (
                <Text color="#A3A3A3" style={{ fontFamily: "Inter" }}>
                  —
                </Text>
              ) : (
                <span
                  className={classes.statusPill}
                  style={{ background: stock.bgColor, color: stock.textColor }}
                >
                  <span
                    className={classes.statusDot}
                    style={{ background: stock.dotColor }}
                  />
                  {stock.daysSupply != null
                    ? `${stock.daysSupply}d · ${stock.label}`
                    : stock.label}
                </span>
              )}
            </td>

            {/* ── Pharmacy ── */}
            <td>
              <Text size="sm" color="#525252" style={{ fontFamily: "Inter" }}>
                {pharmacy}
              </Text>
            </td>

            {/* ── Last Refill ── */}
            <td>
              {lastRefillDate ? (
                <>
                  <Text
                    size="sm"
                    color="#525252"
                    style={{ fontFamily: "Inter" }}
                  >
                    {lastRefillDate}
                  </Text>
                  {lastRefillBy && (
                    <Text
                      size="xs"
                      color="#A3A3A3"
                      mt={2}
                      style={{ fontFamily: "Inter" }}
                    >
                      by {lastRefillBy}
                    </Text>
                  )}
                </>
              ) : (
                <Text color="#A3A3A3" style={{ fontFamily: "Inter" }}>
                  —
                </Text>
              )}
            </td>

            {/* ── Refills Left ── */}
            <td>
              <Text
                size="sm"
                weight={600}
                color="#171717"
                style={{
                  fontFamily: "Inter",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {refillsRemaining}
              </Text>
            </td>

            {/* ── Doctor's Order ── */}
            <td onClick={(e) => e.stopPropagation()}>
              {doctorOrderUrl ? (
                <button
                  className={classes.viewFileBtn}
                  onClick={() => setDoctorOrderModal(doctorOrderUrl)}
                  title="View doctor's order"
                >
                  <IconFileText
                    size={14}
                    color="#525252"
                    style={{ flexShrink: 0 }}
                  />
                  View file
                </button>
              ) : (
                <Text color="#A3A3A3" style={{ fontFamily: "Inter" }}>
                  —
                </Text>
              )}
            </td>

            {/* ── Actions ── */}
            <td
              style={{ textAlign: "right" }}
              onClick={(e) => e.stopPropagation()}
            >
              <Menu shadow="md" position="left" zIndex={1000}>
                <Menu.Target>
                  <ActionIcon radius="md" variant="subtle">
                    <BiDotsVerticalRounded size={20} color="#525252" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item onClick={() => onEditClick?.(row, true)}>
                    View
                  </Menu.Item>
                                    {/* {!isDiscontinued && (!isStaff(profile?.user?.role) || profile?.user?.permissions?.medicationManagement */}
                  {!isDiscontinued && (profile?.user?.permissions?.medicationManagement) && (
                    <Menu.Item onClick={() => onEditClick?.(row, false)}>
                      Edit
                    </Menu.Item>
                  )}
                  {!isDiscontinued &&
                    !isStaff(profile?.user?.role) &&
                    !row?.discontinueNote &&
                    !noCount &&
                    !(
                      row?.refillsLeft !== undefined &&
                      row?.refillsLeft !== "" &&
                      Number(row.refillsLeft) === 0
                    ) && (
                      <Menu.Item onClick={() => setRefillRow(row)}>
                        ReFill
                      </Menu.Item>
                    )}
                  {isDiscontinued && row?.discontinueNote && (
                    <Menu.Item
                      onClick={() => downloadFile(row.discontinueNote)}
                    >
                      Download File
                    </Menu.Item>
                  )}
                  {/* {row?.doctorNote && !isStaff(profile?.user?.role) && (
                    <Menu.Item onClick={() => downloadFile(row.doctorNote)}>
                      Doctor's Note
                    </Menu.Item>
                  )} */}
                  {/* Do not include in design — this is here to explain the logic of the stock bar and days supply. */}
                  {!isStaff(profile?.user?.role) && (
                    <Menu.Item color="red" onClick={() => setDeleteRow(row)}>
                      Delete
                    </Menu.Item>
                  )}
                  {!isDiscontinued && !isStaff(profile?.user?.role) && (
                    <Menu.Item
                      onClick={() => {
                        setSelectDiscontinue(row);
                        open();
                      }}
                    >
                      Discontinue
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            </td>
          </tr>
        );
      })}

      {refillRow && (
        <RefillDrawerStepper
          med={refillRow}
          clientId={clientId}
          onClose={() => setRefillRow(null)}
          onSave={() => {
            setRefillRow(null);
            getMar("all", clientId, false);
            getSinglePatients(clientId);
          }}
        />
      )}

      {/* ── Doctor's Order Preview Modal ── */}
      <Modal
        opened={!!doctorOrderModal}
        onClose={() => setDoctorOrderModal(null)}
        size="xl"
        radius="md"
        zIndex={1100}
        overlayProps={{ blur: 2 }}
        title={
          <Flex align="center" gap={8}>
            <IconFileText size={16} color="#525252" />
            <Text weight={600} style={{ fontFamily: "Inter", fontSize: 14 }}>
              {doctorOrderModal
                ? getFileName(doctorOrderModal)
                : "Doctor's Order"}
            </Text>
          </Flex>
        }
        styles={{
          title: { flex: 1 },
          header: { paddingBottom: 12 },
          body: { paddingTop: 0 },
        }}
      >
        {/* Download button — top right of body */}
        <Flex justify="flex-end" mb={12}>
          <Button
            size="xs"
            variant="outline"
            leftIcon={<IconDownload size={14} />}
            onClick={() => downloadFile(doctorOrderModal)}
            styles={{
              root: {
                borderColor: "#D4D4D4",
                color: "#262626",
                fontFamily: "Inter",
                fontWeight: 600,
                "&:hover": { backgroundColor: "#F5F5F5" },
              },
            }}
          >
            Download
          </Button>
        </Flex>

        {/* Preview area */}
        {doctorOrderModal && (
          <>
            {getFileType(doctorOrderModal) === "image" && (
              <img
                src={doctorOrderModal}
                alt="Doctor's Order"
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                  borderRadius: 8,
                  border: "1px solid #EAECF0",
                  display: "block",
                }}
              />
            )}
            {getFileType(doctorOrderModal) === "pdf" && (
              <iframe
                src={doctorOrderModal}
                title="Doctor's Order"
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "1px solid #EAECF0",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            )}
            {getFileType(doctorOrderModal) === "video" && (
              <video
                src={doctorOrderModal}
                controls
                style={{
                  width: "100%",
                  maxHeight: "70vh",
                  borderRadius: 8,
                  border: "1px solid #EAECF0",
                  display: "block",
                }}
              />
            )}
            {getFileType(doctorOrderModal) === "office" && (
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(doctorOrderModal)}`}
                title="Doctor's Order"
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "1px solid #EAECF0",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            )}
            {getFileType(doctorOrderModal) === "html" && (
              <iframe
                src={doctorOrderModal}
                title="Doctor's Order"
                style={{
                  width: "100%",
                  height: "70vh",
                  border: "1px solid #EAECF0",
                  borderRadius: 8,
                  display: "block",
                }}
              />
            )}
            {getFileType(doctorOrderModal) === "other" && (
              <Flex
                direction="column"
                align="center"
                justify="center"
                gap={10}
                style={{
                  height: 220,
                  background: "#F9FAFB",
                  borderRadius: 8,
                  border: "1px solid #EAECF0",
                }}
              >
                <IconFileText size={48} color="#D4D4D4" />
                <Text
                  weight={500}
                  color="#525252"
                  style={{ fontFamily: "Inter" }}
                >
                  Preview not available for this file type
                </Text>
                <Text size="xs" color="#A3A3A3" style={{ fontFamily: "Inter" }}>
                  Use the Download button above to open the file.
                </Text>
              </Flex>
            )}
          </>
        )}
      </Modal>

      <Modal
        opened={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        centered
        size="sm"
        radius="md"
        zIndex={1000}
        overlayProps={{ blur: 3 }}
        title="Delete Medication"
        styles={{ title: { fontWeight: 700, fontFamily: "Inter" } }}
      >
        <Text size="sm">
          This action is permanent. Deleting medications cannot be undone. Are
          you sure you want to delete this medication?
        </Text>
        <Group grow mt={16}>
          <Button size="xs" color="yellow" onClick={() => setDeleteRow(null)}>
            Cancel
          </Button>
          <Button
            id="confirm-delete"
            size="xs"
            color="red"
            loading={addPatientLoading}
            onClick={async () => {
              await remove(deleteRow?.id, deleteRow?.medicationName);
              setDeleteRow(null);
            }}
          >
            Delete
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default MedicationListTab;
